/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect } from "react";
import { API, CONFIGUREROUTES, LOCAL_STORAGE, DIALOGOPENFOR } from "../../../globals/globals";
import { useGetRfFeedsQuery, useGetConfigStatusQuery, useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { RfFeedsColumns } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { setConfigCounts, setRfFeedList, setConfigStatusData, setRfFeedIndexes } from "../../../store/api/deviceConfig/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { OPERATIONTYPES, constStr, xsdPathStr } from "../../../globals/constants";
import { rfFeedListSelector, selectedRfFeedSelector } from "../../../store/api/deviceConfig/selectors";
import { IFullDeviceDetails, IRfFeed } from "../../../store/api/deviceConfig/types";
import { confirmDialogSelector, paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { prepareRfFeedPayload } from "../../../components/Configuration/helpers";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { initialState } from "../../../store/helpers/helperInitialState";
import { rowSelectionKey } from "../../../components/Actions/ActionHelper";
import { setNoOfSelectedRow } from "../../../store/api/devices/reducers";

export const useDisplayRfFeedData = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const { rfFeed = initialState.pagination } = useSelector(paginationModelSelector);

  const args = { filters: `page=${rfFeed.page.toString()}&size=${rfFeed.pageSize.toString()}`, deviceKey: locationPath[2] };
  const { data, isLoading, refetch } = useGetRfFeedsQuery(args, {
    pollingInterval: API.ENDPOINTS.RFFEEDS.pollingInterval || API.POLLING_INTERVAL,
  });

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const uiseId = localStorage.getItem(LOCAL_STORAGE.USER);
  const args1 = {
    userId: !!uiseId ? uiseId : "",
    tableId: xsdPathStr.rfFeed.tableId,
    deviceObjectKey: locationPath[2],
  };

  const configStatusData = useGetConfigStatusQuery(args1, {
    pollingInterval: API.ENDPOINTS.CONFIGSTATUS.pollingInterval,
  });

  const rfFeedList = useSelector(rfFeedListSelector) as IRfFeed[];
  const selectedRfFeed = useSelector(selectedRfFeedSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const rfFeedIndexes =
    rfFeedList.length > 0 && (rfFeedList.map((item: IRfFeed) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index) as string[]);

  useEffect(() => {
    if (!!data?.data && data?.data.length > 0) {
      dispatch(setRfFeedList(data?.data));
    }
    if (data?.totalItems != null && data?.totalItems >= 0) {
      dispatch(setConfigCounts({ [CONFIGUREROUTES.RFFEED.Path]: data?.totalItems || 0 }));
    }
  }, [data?.data, data?.totalItems, dispatch]);

  useEffect(() => {
    if (!!rfFeedIndexes && rfFeedIndexes?.length > 0) {
      dispatch(setRfFeedIndexes(rfFeedIndexes));
    }
  }, [dispatch, rfFeedIndexes]);

  useEffect(() => {
    if (!!configStatusData.data && configStatusData.data?.length > 0) {
      dispatch(setConfigStatusData(configStatusData.data));
    }
  }, [dispatch, configStatusData.data]);

  const deleteRfFeedHandler = async (actionName: string) => {
    if (!actionName) return;
    const tableRowSelection = rowSelectionKey("rfFeed");
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.RFFEED) {
          const rfFeedPayload =
            !!selectedDevice?.objectKey && prepareRfFeedPayload(undefined, selectedRfFeed, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(rfFeedPayload);
          messageStr = `The deletion process for Rf Feed ${selectedRfFeed?.index} has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          dispatch(setNoOfSelectedRow({ ...tableRowSelection }));
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setNoOfSelectedRow({ ...tableRowSelection }));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    data: rfFeedList,
    isLoading,
    permissions,
    RfFeedsColumns,
    selectedRfFeed,
    configStatusData,
    deleteRfFeedHandler,
  };
};
